import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Grid, Fade } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router";
import { useSnackbar } from "notistack";
import BrandCarousel from "../login/components/BrandCarousel";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { resetState, verifyMagicLink } from "../../redux/slices/loginSlice";

const VerifyMagicLinkForm = () => {
  const [fadeIn, setFadeIn] = useState(false);

  const { verifyMagicLinkError, verifyMagicLinkSuccess, user } = useAppSelector(
    (state) => state.login,
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (token) {
      setTimeout(() => {
        dispatch(verifyMagicLink({ token }));
      }, 1000);
    }
  }, [dispatch, token]);

  useEffect(() => {
    setFadeIn(true);

    if (verifyMagicLinkSuccess) {
      enqueueSnackbar("Secure link verified successfully", {
        variant: "success",
      });

      setFadeIn(false);
      setTimeout(() => {
        navigate(user?.role === "admin" ? "/dashboard" : "/project-home");
      }, 1500);
    }

    if (verifyMagicLinkError) {
      enqueueSnackbar(verifyMagicLinkError, { variant: "error" });
    }
  }, [
    dispatch,
    setFadeIn,
    navigate,
    enqueueSnackbar,
    verifyMagicLinkSuccess,
    verifyMagicLinkError,
    user,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleBackToLogin = useCallback(
    (e: any) => {
      e.preventDefault();

      setFadeIn(false);

      dispatch(resetState());

      setTimeout(() => {
        navigate("/login");
      }, 1000);
    },
    [dispatch, navigate, setFadeIn],
  );

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", p: 2 }}
        >
          <Box
            component="form"
            sx={{
              maxWidth: 350,
              mx: "auto",
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
              },
            }}
          >
            <BrandCarousel />
            <Typography variant="h4" sx={{ fontSize: 24 }} gutterBottom>
              Verifying your secure link
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 15, mb: 2 }}
              gutterBottom
            >
              {verifyMagicLinkError
                ? "Verification failed. Please try again."
                : "Verification is in progress. Please wait..."}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ mt: 2, fontSize: 14 }}
              >
                Go back to{" "}
                <Link
                  onClick={handleBackToLogin}
                  to="/login"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: "bold",
                    fontFamily: "Brandon_Grotesque_bold",
                  }}
                >
                  Log In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{
            display: { xs: "none", md: "block" },
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
              src="/forgot_reset_img.jpg"
              alt="Logo"
            />
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default VerifyMagicLinkForm;
